import 'bootstrap/dist/css/bootstrap.min.css';
import './src/styles/global.scss';
import 'react-international-phone/style.css';

import WrapWithProvider from './wrap-with-provider';
import CookieConsentManager from './src/components/cookiesWidget/CookieConsentManager';
import React from 'react';

const CHUNK_LOAD_RELOAD_KEY = 'hausera-chunk-load-reload';
const PWA_CACHE_RECOVERY_ATTEMPT_KEY = 'hausera-pwa-cache-recovery-attempt';
const PWA_CACHE_RECOVERY_KEY = 'hausera-pwa-cache-recovery';
const PWA_CACHE_RECOVERY_VERSION = '2026-09-25-1';

const getErrorMessage = (error: unknown) => {
	if (error instanceof Error) {
		return `${error.name}: ${error.message}`;
	}

	if (typeof error === 'string') {
		return error;
	}

	if (error && typeof error === 'object' && 'message' in error) {
		return String((error as { message?: unknown }).message);
	}

	return '';
};

const isChunkLoadError = (error: unknown) => {
	const message = getErrorMessage(error);

	return /ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk \d+ failed/i.test(message);
};

const getPwaCacheRecoveryVersion = () => {
	try {
		return localStorage.getItem(PWA_CACHE_RECOVERY_KEY);
	} catch {
		return null;
	}
};

const setPwaCacheRecoveryVersion = () => {
	try {
		localStorage.setItem(PWA_CACHE_RECOVERY_KEY, PWA_CACHE_RECOVERY_VERSION);
	} catch {
		// Storage can fail in restricted mobile browser modes. Cache cleanup still helps.
	}
};

const getSessionValue = (key: string) => {
	try {
		return sessionStorage.getItem(key);
	} catch {
		return null;
	}
};

const setSessionValue = (key: string, value: string) => {
	try {
		sessionStorage.setItem(key, value);
	} catch {
		// If session storage is unavailable, keep the recovery path best-effort.
	}
};

const clearPwaCachesAndReload = async () => {
	setSessionValue(PWA_CACHE_RECOVERY_ATTEMPT_KEY, PWA_CACHE_RECOVERY_VERSION);

	if ('serviceWorker' in navigator) {
		const registrations = await navigator.serviceWorker.getRegistrations();
		await Promise.all(registrations.map((registration) => registration.unregister()));
	}

	if ('caches' in window) {
		const cacheKeys = await caches.keys();
		await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
	}

	setPwaCacheRecoveryVersion();
	window.location.reload();
};

const recoverStalePwaCachesOnce = () => {
	const cacheRecoveryVersion = getPwaCacheRecoveryVersion();
	const cacheRecoveryAttempt = getSessionValue(PWA_CACHE_RECOVERY_ATTEMPT_KEY);

	if (cacheRecoveryVersion === PWA_CACHE_RECOVERY_VERSION || cacheRecoveryAttempt === PWA_CACHE_RECOVERY_VERSION) {
		return;
	}

	clearPwaCachesAndReload().catch(() => {
		setSessionValue(PWA_CACHE_RECOVERY_ATTEMPT_KEY, PWA_CACHE_RECOVERY_VERSION);
		window.location.reload();
	});
};

const recoverOnceAfterChunkLoadError = (error: unknown) => {
	if (!isChunkLoadError(error)) {
		return;
	}

	const currentError = getErrorMessage(error);
	const previousError = getSessionValue(CHUNK_LOAD_RELOAD_KEY);
	const cacheRecoveryVersion = getPwaCacheRecoveryVersion();

	if (previousError === currentError || cacheRecoveryVersion === PWA_CACHE_RECOVERY_VERSION) {
		return;
	}

	setSessionValue(CHUNK_LOAD_RELOAD_KEY, currentError);
	clearPwaCachesAndReload().catch(() => {
		window.location.reload();
	});
};

window.addEventListener('error', (event) => {
	recoverOnceAfterChunkLoadError(event.error || event.message);
});

window.addEventListener('unhandledrejection', (event) => {
	recoverOnceAfterChunkLoadError(event.reason);
});

const registerHauseraServiceWorker = () => {
	if (!('serviceWorker' in navigator)) {
		return;
	}

	if (getPwaCacheRecoveryVersion() !== PWA_CACHE_RECOVERY_VERSION) {
		return;
	}

	navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {
		// Restricted browser contexts can reject service worker registration.
	});
};

recoverStalePwaCachesOnce();

// Escuchar el consentimiento inicial o cambios en el consentimiento
window.addEventListener('cc:onConsent', ({ detail }: any) => {
	console.log('Consentimiento otorgado:', detail.cookie);
	const { categories } = detail.cookie;

	// Determinar si cada categoría ha sido aceptada
	const analyticsConsent = categories.includes('analytics') ? 'C0002' : '';
	const advertisingConsent = categories.includes('advertisement') ? 'C0003' : '';
	const personalizationConsent = categories.includes('personalization') ? 'C0004' : '';

	// Preparar el objeto para el Data Layer
	const consentDetails = {
		event: 'CookieConsentUpdates',
		consent: {
			analytics: analyticsConsent,
			advertising: advertisingConsent,
			personalization: personalizationConsent,
		},
	};

	// Enviar al Data Layer
	(window as any).dataLayer = (window as any).dataLayer || [];
	(window as any).dataLayer.push(consentDetails);
});

// Escuchar cambios en las preferencias de consentimiento
window.addEventListener('cc:onChange', ({ detail }: any) => {
	console.log('Consentimiento modificado:', detail.changedCategories, detail.changedServices);
	const { categories } = detail.cookie;

	const analyticsConsent = categories.includes('analytics') ? 'C0002' : '';
	const advertisingConsent = categories.includes('advertisement') ? 'C0003' : '';
	const personalizationConsent = categories.includes('personalization') ? 'C0004' : '';

	const consentDetails = {
		event: 'CookieConsentUpdates',
		consent: {
			analytics: analyticsConsent,
			advertisement: advertisingConsent,
			personalization: personalizationConsent,
		},
	};

	(window as any).dataLayer.push(consentDetails);
});

// // gatsby-browser.js
// window.onerror = function (message, source, lineno, colno, error) {
//     analytics.track({
//       userId: 'userId', // O alguna identificación del usuario si está disponible
//       event: 'Frontend Error',
//       properties: {
//         message: message,
//         source: source,
//         line: lineno,
//         column: colno,
//         error: error.stack,
//         // Otra información relevante
//       }
//     });
//   };

export const onClientEntry = () => {
	registerHauseraServiceWorker();
};

export const wrapRootElement = ({ element }: { element: React.ReactNode }) => {
	return <WrapWithProvider element={<CookieConsentManager>{element}</CookieConsentManager>} />;
};
